import React, { useMemo } from 'react'
import styled, { css } from 'styled-components'

import { Panel } from 'components/Panel'
import CollapsiblePanel from 'components/CollapsiblePanel'
import TriggeringAlertsPanel from 'pages/Device/ThermostatRthPanel/TriggeringAlertsPanel'

import { highestSeverity, alertCodeDisplayValue, sortBasedOnDate } from 'helpers/alerts'

import { DeviceConsolidatedEvent } from 'types/ConsolidatedEvent'

import { color } from 'styles/colors'

import GA4 from 'helpers/ga4'
import Apptentive from 'helpers/apptentive'

interface Props {
  consolidatedEvents: DeviceConsolidatedEvent[]
}

const ConsolidatedEventsBanner = ({ consolidatedEvents }: Props) => {
  const highestSeverityOfTriggeringAlerts = useMemo(() => {
    return highestSeverity(
      consolidatedEvents.map(event => event.triggeringAlertDescriptions).flat()
    ) ?? ''
  }, [consolidatedEvents])

  const sortedConsolidatedEvents = useMemo(() => sortBasedOnDate(consolidatedEvents, 'date'), [consolidatedEvents])

  if (consolidatedEvents?.length < 1) {
    return null
  }

  const panelExpanded = () => {
    GA4.sendEvent({ category: 'Customer Detail Page', action: 'Expand Consolidated Events Banner' })
    Apptentive.sendEvent('expand_consolidated_events_banner')
  }

  return (
    <S.CollapsiblePanel
      expandedCallback={panelExpanded}
      headerComponent={
        <S.Header severity={highestSeverityOfTriggeringAlerts}>
          <Panel.Title>Consolidated Events ({consolidatedEvents?.length})</Panel.Title>
        </S.Header>
      }
      caretSize="24px"
      caretHorizontalPosition="right"
      severity={highestSeverityOfTriggeringAlerts}
    >
      <S.ConsolidatedEventsList>
        {sortedConsolidatedEvents?.map(event => {
          const sortedTriggeringAlertCodes = event.uniqueTriggeringAlertCodes
            .map(code => alertCodeDisplayValue(code))
            .sort()
            .join(', ')

          return (
            <CollapsiblePanel
              key={`${event.name}-${event.createdAt}`}
              headerComponent={
                <S.EventHeader>
                  <S.Subtitle>{event.name}</S.Subtitle>
                </S.EventHeader>
              }
            >
              <S.EventContent>
                <S.Subtitle>Problem</S.Subtitle>

                <S.Subsection>
                  <div>Diagnostics has detected a(n) {event.name.toLowerCase()}</div>
                  <div>The following alerts may occur because of this event:</div>
                  <div>{sortedTriggeringAlertCodes}</div>
                  <div>Note that you may also have received notifications for the triggering alerts</div>
                </S.Subsection>
              </S.EventContent>

              <S.EventContent>
                <S.Subtitle>Possible causes and solutions</S.Subtitle>
                <S.Subsection>
                  <S.List itemsCount={event.possibleCauses?.length ?? 0}>
                    {event.possibleCauses
                      ? event.possibleCauses.map(({ description, details }) =>
                        (
                          <div key={description}>
                            <li>{description}</li>
                            {details && <ul> {details.map(detail => <li key={detail}>{detail}</li>)}</ul>}
                          </div>
                        )
                      )
                      : '--'
                    }
                  </S.List>
                </S.Subsection>
              </S.EventContent>

              <TriggeringAlertsPanel alertDescriptions={event.triggeringAlertDescriptions} />
            </CollapsiblePanel>
          )
        })}
      </S.ConsolidatedEventsList>
    </S.CollapsiblePanel>
  )
}

export default ConsolidatedEventsBanner

const headerColor = ({ severity }) => {
  return css`
    background-color: ${color(severity ? `${severity}Alert` : 'devicePanelHeader')};
    color: ${color(severity === 'major' ? 'primaryText' : 'devicePanelHeaderText')}
  `
}

const S = {
  Header: styled(Panel.Header).withConfig({
    shouldForwardProp: (prop) => !['severity'].includes(prop)
  })<{ severity: string }>`
    padding-right: 0px;
    flex: 1;
    ${headerColor}
  `,
  // TODO: refactor CollapsiblePanel so that nested css selectors aren't required to style it
  CollapsiblePanel: styled(CollapsiblePanel).withConfig({
    shouldForwardProp: (prop) => !['severity'].includes(prop)
  })<{ severity: string }>`
    & > div:first-of-type {
      ${headerColor}

      & > div:first-of-type {
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
        ${headerColor}
      }

      & > i:first-of-type {
        ${headerColor}
      }
    }

    & > div:last-of-type {
      padding: 0px;
    }
  `,
  ConsolidatedEventsList: styled.div`
    margin-bottom: 20px;
    border: ${color('border')} solid 1px;
    border-top: none;

    & > :nth-child(2n) {
      background-color: ${color('infoPanelHeaderBackground')};
    }
  `,
  EventHeader: styled.div`
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    padding: 10px;
  `,
  EventDate: styled.span`
    text-align: right;
  `,
  EventContent: styled.div`
    padding-left: 25px;
  `,
  Subtitle: styled.div`
    font-weight: bold;
    text-transform: capitalize;
  `,
  Subsection: styled.div`
    padding: 5px 0 15px 15px;
  `,
  List: styled.ul.withConfig({
    shouldForwardProp: (prop) => !['itemsCount'].includes(prop)
  })<{ itemsCount: number }>`
    list-style-type: ${({ itemsCount }) => itemsCount > 1 ? 'disc' : 'none'};
  `
}
